import React from 'react'
import {
  CheckIcon,
  DotIcon,
  EditIcon,
  LightbulbIcon,
  RefreshCwIcon,
  SparklesIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import {
  Label as ChartLabel,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
} from 'recharts'

import { type ResumeAlphaData } from '@/components/hooks/use-resume-tailor'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ChartConfig, ChartContainer } from '@/components/ui/chart'
import { Checkbox } from '@/components/ui/checkbox'
import { Item, ItemActions, ItemContent, ItemTitle } from '@/components/ui/item'
import { Textarea } from '@/components/ui/textarea'

import { cn } from '@/lib/utils'

export const AnalysisStage = (props: {
  data: ResumeAlphaData
  setData: React.Dispatch<React.SetStateAction<ResumeAlphaData>>
  isStreaming?: boolean
  isMovingToNextStage?: boolean
  onNextStep?: () => void
}) => {
  const { data, setData, isStreaming, isMovingToNextStage } = props

  const endRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (isStreaming) {
      endRef.current?.scrollIntoView({
        behavior: 'instant',
        block: 'center',
        inline: 'nearest',
      })
    }
  }, [
    isStreaming,
    data.analysis?.ats_score,
    data.analysis?.reasonings,
    data.analysis?.improvements,
    data.analysis?.custom_request?.enabled,
  ])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resume Analysis</CardTitle>
        <CardDescription>
          Comprehensive evaluation of your resume against job requirements
        </CardDescription>
      </CardHeader>
      <CardContent>
        {(data?.analysis?.ats_score || data?.analysis?.reasonings) && (
          <div className="mb-4 grid gap-4 lg:grid-cols-3">
            {data?.analysis?.ats_score && (
              <div className="lg:col-span-1">
                <Card className="h-full">
                  <CardContent className="h-full">
                    <ATSScoreChart percent={data?.analysis?.ats_score || 0} />
                  </CardContent>
                </Card>
              </div>
            )}

            {data?.analysis?.reasonings && (
              <div className="lg:col-span-2">
                <MatchReasons reasonings={data?.analysis?.reasonings || []} />
              </div>
            )}
          </div>
        )}
        {data?.analysis?.improvements && (
          <ImprovementsList
            isStreaming={isStreaming}
            improvements={data?.analysis?.improvements || []}
            onValueChange={(value) =>
              setData((prev) => ({
                ...prev,
                analysis: { ...prev.analysis, improvements: value },
              }))
            }
          />
        )}
        {!isStreaming && data?.analysis?.custom_request?.enabled && (
          <Card
            className="relative my-4"
            ref={(el) => {
              if (el)
                document.body.scrollIntoView({
                  behavior: 'instant',
                  block: 'end',
                })
            }}
          >
            <Button
              className="absolute top-4 right-4"
              size="sm"
              variant={'destructive'}
              onClick={() =>
                setData((prev) => ({
                  ...prev,
                  analysis: {
                    ...prev.analysis,
                    custom_request: {
                      ...prev.analysis?.custom_request,
                      enabled: false,
                    },
                  },
                }))
              }
            >
              Cancel
            </Button>
            <CardHeader>
              <h2 className="text-lg font-semibold text-foreground">
                Custom Resume Requests
              </h2>
            </CardHeader>
            <CardContent>
              <Textarea
                value={data?.analysis?.custom_request.value}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    analysis: {
                      ...prev.analysis,
                      custom_request: {
                        ...prev.analysis?.custom_request,
                        value: e.target.value,
                      },
                    },
                  }))
                }
                placeholder="Any special requests/conditions you want to enforce. ie.&#10;- Hard-limit to 1 page; combine bullets if needed.&#10;- No bullet may exceed 12 words.&#10;- Spell out AWS services once, then use abbreviations: Amazon EC2 → EC2.&#10;- Anonymize employer names: replace 'JPMorgan' with 'Tier-1 Investment Bank'."
                className="max-h-50 min-h-50 w-full resize-none"
              />
            </CardContent>
          </Card>
        )}
        <div ref={endRef} />
      </CardContent>
      <CardFooter className="flex flex-row justify-center gap-4">
        {!data?.analysis?.custom_request?.enabled && (
          <Button
            size="lg"
            className={'cursor-pointer'}
            disabled={isMovingToNextStage || isStreaming}
            onClick={() =>
              setData((prev) => ({
                ...prev,
                analysis: {
                  ...prev.analysis,
                  custom_request: {
                    ...prev.analysis?.custom_request,
                    enabled: true,
                  },
                },
              }))
            }
          >
            Add Custom Resume Request
          </Button>
        )}
        {createPortal(
          <div>
            <Button
              onClick={() => props?.onNextStep && props.onNextStep()}
              size="lg"
              className="cursor-pointer gap-2"
              disabled={isMovingToNextStage || isStreaming}
            >
              {isMovingToNextStage ? (
                <>
                  <RefreshCwIcon className="h-4 w-4 animate-spin" />
                  Preparing to Tailor...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-4 w-4" />
                  Tailor My Resume
                </>
              )}
            </Button>
          </div>,
          document.getElementById('control-bar-step-action') as Element,
        )}
      </CardFooter>
    </Card>
  )
}

function MatchReasons({ reasonings }: { reasonings: string[] }) {
  const isPositive = (text: string) => {
    const negativeIndicators = [
      'no ',
      'not ',
      'missing',
      'lacking',
      'absence',
      'without',
    ]
    return !negativeIndicators.some((indicator) =>
      text.toLowerCase().includes(indicator),
    )
  }

  const positiveReasons = reasonings.filter(isPositive)
  const negativeReasons = reasonings.filter((r) => !isPositive(r))

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Match Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        {positiveReasons.length > 0 && (
          <div className="space-y-1.5">
            <h3 className="flex items-center gap-1.5 font-medium text-emerald-600 dark:text-emerald-400">
              <CheckIcon className="h-3.5 w-3.5" />
              Strengths ({positiveReasons.length})
            </h3>
            {positiveReasons.map((reason, index) => (
              <div key={`pos-${index}`} className="group flex items-center">
                <DotIcon className="text-emerald-500" />
                <p className="text-xs leading-relaxed text-foreground">
                  {reason}
                </p>
              </div>
            ))}
          </div>
        )}
        {negativeReasons.length > 0 && (
          <div className="space-y-1.5 pt-1">
            <h3 className="flex items-center gap-1.5 font-medium text-amber-600 dark:text-amber-400">
              <XIcon className="h-3.5 w-3.5" />
              Gaps ({negativeReasons.length})
            </h3>
            {negativeReasons.map((reason, index) => (
              <div key={`neg-${index}`} className="group flex items-center">
                <DotIcon className="text-amber-500" />
                <p className="text-xs leading-relaxed text-foreground">
                  {reason}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ImprovementsList({
  isStreaming,
  improvements,
  ...props
}: {
  isStreaming?: boolean
  improvements: { checked: boolean; value: string; defaultValue: string }[]
  onValueChange: (
    value: { checked: boolean; value: string; defaultValue: string }[],
  ) => void
}) {
  const [editTracker, setEditTracker] = React.useState<
    Record<string, { enabled: boolean; value: string }>
  >({})

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-row items-center gap-1">
          <LightbulbIcon className="h-4 w-4 text-primary" />
          Recommended Improvements
        </CardTitle>
        <CardDescription>
          Action items to improve your ATS score and increase your chances of
          landing an interview
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 sm:grid-cols-2">
          {improvements.map((improv, index) => (
            <Item
              key={`${index}-${improv.value.slice(0, 10)}`}
              className="flex flex-col-reverse"
              size="sm"
              variant={improv.checked ? 'outline' : 'muted'}
            >
              <ItemContent className="flex w-full">
                {editTracker[`${index}` as keyof typeof editTracker]
                  ?.enabled === true ? (
                  <div className="flex flex-col gap-1">
                    <Textarea
                      defaultValue={improv.value}
                      onChange={(e) =>
                        setEditTracker((prev) => ({
                          ...prev,
                          [`${index}`]: {
                            ...prev[index],
                            value: e.target.value,
                          },
                        }))
                      }
                    />
                    <div className="flex flex-row items-center justify-end gap-2">
                      <Button
                        onClick={() => {
                          setEditTracker((prev) => ({
                            ...prev,
                            [`${index}`]: { enabled: false, value: '' },
                          }))
                        }}
                        size="sm"
                        variant="destructive"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          props?.onValueChange &&
                            props.onValueChange(
                              improvements.map((curr, idx) =>
                                idx === index
                                  ? { ...curr, value: editTracker[index].value }
                                  : curr,
                              ),
                            )
                          setEditTracker((prev) => ({
                            ...prev,
                            [`${index}`]: { enabled: false, value: '' },
                          }))
                        }}
                        size="sm"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <ItemTitle
                    className={cn(
                      !improv.checked
                        ? 'text-muted-foreground'
                        : 'text-foreground',
                    )}
                  >
                    {improv.value}
                  </ItemTitle>
                )}
              </ItemContent>
              <ItemActions className="flex w-full flex-row justify-end">
                <Checkbox
                  checked={improv.checked}
                  onCheckedChange={(check) => {
                    props.onValueChange(
                      improvements.map((curr, idx) =>
                        idx === index ? { ...curr, checked: check } : curr,
                      ),
                    )
                  }}
                  className={'cursor-pointer'}
                />
                <Button
                  variant="outline"
                  size="xs"
                  className={'cursor-pointer'}
                  onClick={() => {
                    setEditTracker((prev) => ({
                      ...prev,
                      [`${index}`]: { enabled: true, value: '' },
                    }))
                  }}
                >
                  <EditIcon />
                </Button>
                <Button
                  variant="destructive"
                  size="xs"
                  className={'cursor-pointer'}
                  onClick={() => {
                    props.onValueChange(
                      improvements.filter((_, idx) => idx !== index),
                    )
                  }}
                >
                  <Trash2Icon />
                </Button>
              </ItemActions>
            </Item>
          ))}
        </div>
        <div className="mx-auto flex flex-row justify-center py-6 pb-0">
          <Button
            disabled={isStreaming}
            className="cursor-pointer"
            onClick={() =>
              props.onValueChange([
                ...improvements,
                {
                  checked: true,
                  value: 'Edit to add your suggestion...',
                  defaultValue: 'string',
                },
              ])
            }
          >
            Add Suggestion
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ATSScoreChart(props: {
  percent: number
  className?: string
  width?: number | string
  height?: number | string
}) {
  const [displayPercent, setDisplayPercent] = React.useState(0)

  React.useEffect(() => {
    let start = 0
    const end = props.percent
    const duration = 1000 // 1 second animation
    const increment = end / (duration / 16) // 60fps approx

    const timer = setInterval(() => {
      start += increment
      if (start >= end) {
        setDisplayPercent(end)
        clearInterval(timer)
      } else {
        setDisplayPercent(Math.floor(start))
      }
    }, 16)

    return () => clearInterval(timer)
  }, [props.percent])

  const chartData = [
    { browser: 'safari', percentage: 72, fill: 'var(--color-safari)' },
  ]

  const chartConfig = {
    percentage: {
      label: 'Percentage',
    },
    safari: {
      label: 'Safari',
      color: 'var(--chart-2)',
    },
  } satisfies ChartConfig

  return (
    <ChartContainer
      config={chartConfig}
      className={cn('mx-auto aspect-square max-h-62.5', props.className)}
    >
      <RadialBarChart
        data={chartData}
        startAngle={270}
        endAngle={270 - 360 * (displayPercent / 100)}
        innerRadius={80}
        outerRadius={110}
        barSize={4}
      >
        <PolarGrid
          gridType="circle"
          radialLines={false}
          stroke="none"
          className="first:fill-muted last:fill-background"
          polarRadius={[86, 74]}
        />
        <PolarRadiusAxis
          type="number"
          domain={[0, 100]}
          tick={false}
          tickLine={false}
          axisLine={false}
        >
          <ChartLabel
            content={({ viewBox }) => {
              if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className="fill-foreground text-xl font-bold"
                    >
                      {displayPercent}%
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 24}
                      className="fill-muted-foreground"
                    >
                      ATS Score
                    </tspan>
                  </text>
                )
              }
            }}
          />
        </PolarRadiusAxis>
        <RadialBar
          dataKey="percentage"
          background
          cornerRadius={10}
          fill="var(--color-safari)"
        />
      </RadialBarChart>
    </ChartContainer>
  )
}
